import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  User, Briefcase, Award, Globe, MessageSquare, 
  ArrowRight, ArrowLeft, Check, Sparkles, Loader2, Upload,
  Linkedin, Twitter, Mail, Phone, Building2, Target, Eye,
  BookOpen, Mic, Trophy, Lightbulb, X, Save, Send, Play, Clock,
  Gift, AlertTriangle, TrendingUp, MousePointer, BarChart3, Share2,
  Link as LinkIcon, Youtube, Video
} from 'lucide-react';
import ProfileScoreWidget from '../components/ProfileScoreWidget';
import OpportunityManager from '../components/OpportunityManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LeadershipProfileEdit() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { profileId } = useParams();
  const photoInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Testimonials state
  const [testimonials, setTestimonials] = useState([]);
  const [showTestimonialModal, setShowTestimonialModal] = useState(false);
  const [testimonialRequest, setTestimonialRequest] = useState({ contact_name: '', contact_email: '', message: '' });
  const [isRequestingTestimonial, setIsRequestingTestimonial] = useState(false);
  const [togglingVisibility, setTogglingVisibility] = useState(null);
  
  // Subscription & Coupon state
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [activeUntil, setActiveUntil] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponValidation, setCouponValidation] = useState(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('6_months');
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [showCallbackForm, setShowCallbackForm] = useState(false);
  const [callbackPhone, setCallbackPhone] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  
  const [formData, setFormData] = useState({
    leadership_mode: 'company_leadership',
    profile_category: 'general',
    leader_name: '',
    leader_title: '',
    leader_photo: '',
    tagline: '',
    company_name: '',
    company_role: '',
    vision_statement: '',
    leadership_philosophy: '',
    career_journey: '',
    years_experience: '',
    expertise_areas: [],
    industries: [],
    achievements: '',
    awards: [],
    certifications: [],
    thought_leadership_topics: [],
    profile_sections: null,
    contact_email: '',
    contact_phone: '',
    linkedin_url: '',
    twitter_url: '',
    website: '',
    youtube_url: '',
    instagram_url: '',
    
    // Videos
    additional_videos: [],
    
    // Published Articles/Media
    published_articles: []
  });

  const [originalSlug, setOriginalSlug] = useState('');
  
  // Personal profile categories
  const personalCategories = [
    { id: 'general', label: 'Professional', description: 'General professional profile' },
    { id: 'board_level', label: 'Board Level Executive', description: 'Board directors, non-exec roles' },
    { id: 'founder', label: 'Founder / Entrepreneur', description: 'Startup founders, business builders' },
    { id: 'diplomatic', label: 'Diplomatic / Government', description: 'Government officials, diplomats' },
    { id: 'consultant', label: 'Consultant / Advisory', description: 'Management consultants, advisors' },
    { id: 'ca_finance', label: 'CA / Finance Professional', description: 'Chartered accountants, CFOs' },
    { id: 'medical', label: 'Medical Professional', description: 'Doctors, surgeons, healthcare leaders' },
    { id: 'legal', label: 'Legal Professional', description: 'Lawyers, legal counsel, judges' },
    { id: 'academic', label: 'Academic / Researcher', description: 'Professors, researchers, scholars' },
    { id: 'creative', label: 'Creative / Media', description: 'Artists, filmmakers, creative directors' },
    { id: 'tech_leader', label: 'Technology Leader', description: 'CTOs, tech executives, engineers' },
    { id: 'investor', label: 'Investor / VC', description: 'VCs, angel investors, PE professionals' },
    { id: 'speaker', label: 'Speaker / Thought Leader', description: 'Keynote speakers, authors' }
  ];

  // Fetch existing profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/leadership-profiles/${profileId}`, {
          withCredentials: true
        });
        const profile = response.data;
        setFormData({
          leadership_mode: profile.leadership_mode || 'company_leadership',
          profile_category: profile.profile_category || 'general',
          leader_name: profile.leader_name || '',
          leader_title: profile.leader_title || '',
          leader_photo: profile.photo_url || '',
          tagline: profile.tagline || '',
          company_name: profile.company_name || '',
          company_role: profile.company_role || '',
          vision_statement: profile.vision_statement || '',
          leadership_philosophy: profile.leadership_philosophy || '',
          career_journey: profile.career_journey || '',
          years_experience: profile.years_experience || '',
          expertise_areas: profile.expertise_areas || [],
          industries: profile.industries || [],
          achievements: profile.achievements || '',
          awards: profile.awards || [],
          certifications: profile.certifications || [],
          thought_leadership_topics: profile.thought_leadership_topics || [],
          profile_sections: profile.profile_sections || null,
          contact_email: profile.contact_email || '',
          contact_phone: profile.contact_phone || '',
          linkedin_url: profile.linkedin_url || '',
          twitter_url: profile.twitter_url || '',
          website: profile.website || '',
          youtube_url: profile.youtube_url || '',
          instagram_url: profile.instagram_url || '',
          additional_videos: profile.additional_videos || [],
          published_articles: profile.published_articles || []
        });
        setOriginalSlug(profile.slug);
      } catch (err) {
        setError('Failed to load profile. It may not exist or you may not have permission.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfile();
  }, [profileId]);
  
  // Fetch testimonials
  useEffect(() => {
    const fetchTestimonials = async () => {
      if (!profileId) return;
      try {
        const response = await axios.get(`${API_URL}/api/leadership-profiles/${profileId}/testimonials`, { withCredentials: true });
        setTestimonials(response.data.testimonials || []);
      } catch (err) {
        console.log('No testimonials found');
      }
    };
    
    if (profileId) {
      fetchTestimonials();
    }
  }, [profileId]);
  
  // Fetch subscription status and analytics
  useEffect(() => {
    const fetchSubscriptionAndAnalytics = async () => {
      if (!profileId) return;
      try {
        // Fetch profile to get subscription status
        const profileRes = await axios.get(`${API_URL}/api/leadership-profiles/${profileId}`, { withCredentials: true });
        const profile = profileRes.data;
        setSubscriptionStatus(profile.subscription_status || 'inactive');
        setActiveUntil(profile.active_until ? new Date(profile.active_until) : null);
        
        // Fetch analytics
        const analyticsRes = await axios.get(`${API_URL}/api/leadership-profiles/${profileId}/analytics`, { withCredentials: true });
        setAnalytics(analyticsRes.data);
      } catch (err) {
        console.log('Could not fetch subscription/analytics');
      }
    };
    
    if (profileId) {
      fetchSubscriptionAndAnalytics();
    }
  }, [profileId]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
    setSuccess('');
  };

  const handleArrayInput = (field, value) => {
    const items = value.split(',').map(item => item.trim()).filter(Boolean);
    setFormData(prev => ({ ...prev, [field]: items }));
  };

  // Video management functions
  const addVideoSlot = () => {
    if (formData.additional_videos.length < 5) {
      setFormData(prev => ({
        ...prev,
        additional_videos: [...prev.additional_videos, { type: 'youtube', url: '', title: '' }]
      }));
    }
  };

  const updateVideo = (index, field, value) => {
    setFormData(prev => {
      const videos = [...prev.additional_videos];
      videos[index] = { ...videos[index], [field]: value };
      return { ...prev, additional_videos: videos };
    });
  };

  const removeVideo = (index) => {
    setFormData(prev => ({
      ...prev,
      additional_videos: prev.additional_videos.filter((_, i) => i !== index)
    }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingPhoto(true);
    const photoFormData = new FormData();
    photoFormData.append('file', file);
    
    try {
      const response = await axios.post(`${API_URL}/api/upload/image`, photoFormData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      handleInputChange('leader_photo', response.data.url);
    } catch (err) {
      setError('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const regenerateSections = async () => {
    // Confirmation dialog if there's existing content
    const hasExistingContent = formData.profile_sections && (
      formData.profile_sections.who_i_am || 
      formData.profile_sections.my_journey || 
      formData.profile_sections.my_expertise ||
      formData.profile_sections.my_vision
    );
    
    if (hasExistingContent) {
      const confirmed = window.confirm(
        '⚠️ This will replace your current profile sections with new AI-generated content.\n\n' +
        'Any manual edits you\'ve made will be lost.\n\n' +
        'Are you sure you want to regenerate?'
      );
      if (!confirmed) return;
    }
    
    setIsGenerating(true);
    setError('');
    
    try {
      const response = await axios.post(`${API_URL}/api/leadership-profiles/generate-sections`, {
        leader_name: formData.leader_name,
        leader_title: formData.leader_title,
        company_name: formData.company_name,
        leadership_mode: formData.leadership_mode,
        vision_statement: formData.vision_statement,
        career_journey: formData.career_journey,
        expertise_areas: formData.expertise_areas,
        achievements: formData.achievements
      }, { withCredentials: true });
      
      handleInputChange('profile_sections', response.data.profile_sections);
      setSuccess('AI sections regenerated successfully!');
    } catch (err) {
      setError('Failed to regenerate profile sections');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!formData.leader_name.trim()) {
      setError('Name is required');
      return;
    }
    if (!formData.leader_title.trim()) {
      setError('Title is required');
      return;
    }
    
    setIsSaving(true);
    setError('');
    setSuccess('');
    
    try {
      await axios.put(`${API_URL}/api/leadership-profiles/${profileId}`, {
        ...formData,
        photo_url: formData.leader_photo
      }, {
        withCredentials: true
      });
      
      setSuccess('Profile updated successfully!');
      setTimeout(() => {
        navigate(`/l/${originalSlug}`);
      }, 1500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', '));
      } else {
        setError(detail || 'Failed to update profile');
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Helper to normalize API error messages (handles 422 array errors)
  const getApiErrorMessage = (err) => {
    const detail = err.response?.data?.detail;
    if (!detail) return 'An error occurred';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
    }
    return 'An error occurred';
  };

  const expertiseOptions = [
    'Strategy', 'Operations', 'Finance', 'Marketing', 'Sales', 'Technology',
    'Product', 'HR', 'Legal', 'Supply Chain', 'R&D', 'Innovation',
    'Digital Transformation', 'Sustainability', 'M&A', 'International Business'
  ];

  const industryOptions = [
    'Technology', 'Healthcare', 'Finance', 'Manufacturing', 'Retail', 
    'Energy', 'Pharma', 'FMCG', 'Automotive', 'Real Estate', 'Education',
    'Media', 'Telecommunications', 'Agriculture', 'Logistics'
  ];
  
  // Testimonial functions
  const requestTestimonial = async () => {
    if (!testimonialRequest.contact_name || !testimonialRequest.contact_email) {
      setError('Contact name and email are required');
      return;
    }
    
    setIsRequestingTestimonial(true);
    try {
      await axios.post(
        `${API_URL}/api/leadership-profiles/${profileId}/request-testimonial`,
        testimonialRequest,
        { withCredentials: true }
      );
      
      // Refresh testimonials list
      const response = await axios.get(`${API_URL}/api/leadership-profiles/${profileId}/testimonials`, { withCredentials: true });
      setTestimonials(response.data.testimonials || []);
      
      setShowTestimonialModal(false);
      setTestimonialRequest({ contact_name: '', contact_email: '', message: '' });
      setError('');
      setSuccess('Testimonial request sent successfully!');
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setIsRequestingTestimonial(false);
    }
  };
  
  const toggleTestimonialVisibility = async (testimonialId) => {
    setTogglingVisibility(testimonialId);
    try {
      const response = await axios.put(
        `${API_URL}/api/testimonials/${testimonialId}/visibility`,
        {},
        { withCredentials: true }
      );
      
      // Update local state
      setTestimonials(prev => prev.map(t => 
        t.id === testimonialId ? { ...t, is_visible: response.data.is_visible } : t
      ));
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setTogglingVisibility(null);
    }
  };
  
  // Coupon validation
  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setIsValidatingCoupon(true);
    setCouponValidation(null);
    try {
      const response = await axios.post(`${API_URL}/api/coupons/validate?coupon_code=${couponCode.toUpperCase()}&plan=6_months`);
      setCouponValidation(response.data);
    } catch (err) {
      setCouponValidation({ 
        valid: false, 
        error: err.response?.data?.detail || 'Invalid coupon code' 
      });
    } finally {
      setIsValidatingCoupon(false);
    }
  };
  
  // Apply coupon to activate subscription
  const applyCoupon = async () => {
    if (!couponValidation?.valid) return;
    
    setIsApplyingCoupon(true);
    try {
      const response = await axios.post(`${API_URL}/api/coupons/apply`, {
        coupon_code: couponCode.toUpperCase(),
        profile_id: profileId,
        profile_type: 'leadership'
      }, { withCredentials: true });
      
      // Update local subscription status
      setSubscriptionStatus('active');
      setActiveUntil(new Date(response.data.subscription.active_until));
      setCouponCode('');
      setCouponValidation(null);
      
      // Show success with clear guidance
      setError('');
      alert('Profile activated with All-Access! All features are now unlocked. The page will refresh to apply changes.');
      window.location.reload();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setIsApplyingCoupon(false);
    }
  };
  
  // Calculate days until expiry
  const getDaysUntilExpiry = () => {
    if (!activeUntil) return null;
    const now = new Date();
    const diff = activeUntil - now;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };
  
  const daysUntilExpiry = getDaysUntilExpiry();

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C41E3A]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2">Edit Leadership Profile</h1>
            <p className="text-zinc-400">Update your professional story</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4 inline mr-2" />
              Back to Admin
            </button>
            <a
              href={`/l/${originalSlug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
            >
              <Eye className="w-4 h-4 inline mr-2" />
              View Live
            </a>
          </div>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
            <X className="w-5 h-5" />
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-500/10 border border-green-500/30 text-green-400 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
            <Check className="w-5 h-5" />
            {success}
          </div>
        )}
        
        {/* Subscription Expiry Alert */}
        {subscriptionStatus === 'active' && daysUntilExpiry !== null && daysUntilExpiry <= 30 && (
          <div className={`mb-6 rounded-lg p-4 flex items-center justify-between ${
            daysUntilExpiry <= 0 
              ? 'bg-red-900/30 border border-red-800' 
              : daysUntilExpiry <= 7 
              ? 'bg-red-900/20 border border-red-700' 
              : 'bg-amber-900/20 border border-amber-700'
          }`} data-testid="subscription-expiry-alert">
            <div className="flex items-center gap-3">
              <AlertTriangle className={`w-5 h-5 ${daysUntilExpiry <= 7 ? 'text-red-400' : 'text-amber-400'}`} />
              <div>
                <p className={`font-medium ${daysUntilExpiry <= 7 ? 'text-red-400' : 'text-amber-400'}`}>
                  {daysUntilExpiry <= 0 
                    ? 'Your profile subscription has expired!' 
                    : daysUntilExpiry <= 7 
                    ? `Urgent: Your profile expires in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? '' : 's'}!`
                    : `Your profile expires in ${daysUntilExpiry} days`}
                </p>
                <p className="text-zinc-400 text-sm">
                  {daysUntilExpiry <= 0 
                    ? 'Your profile is no longer visible to the public. Renew to make it live again.'
                    : 'Renew your subscription to keep your profile live and visible.'}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {subscriptionStatus === 'inactive' && (
          <div className="mb-6 bg-blue-900/20 border border-blue-700 rounded-lg p-4 flex items-center justify-between" data-testid="subscription-inactive-alert">
            <div className="flex items-center gap-3">
              <Gift className="w-5 h-5 text-blue-400" />
              <div>
                <p className="font-medium text-blue-400">Profile Not Yet Live</p>
                <p className="text-zinc-400 text-sm">Activate your profile with a promo code to make it visible to the public.</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Analytics Dashboard (if active and has data) */}
        {analytics && subscriptionStatus === 'active' && (
          <div className="mb-6 bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden" data-testid="analytics-dashboard">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#C41E3A]/20 to-zinc-900 px-5 py-4 border-b border-zinc-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#C41E3A]" />
                  <span className="text-white font-semibold">Profile Analytics</span>
                </div>
                <span className="text-zinc-500 text-xs">
                  Active until {activeUntil?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              </div>
            </div>
            
            {/* Main Stats Grid */}
            <div className="p-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {/* Total Views */}
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="w-4 h-4 text-blue-400" />
                    <span className="text-zinc-400 text-xs">Profile Views</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{analytics.views?.total || 0}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-zinc-500">Today: {analytics.views?.today || 0}</span>
                    {analytics.views?.this_week > 0 && (
                      <span className="text-xs text-emerald-400 flex items-center gap-0.5">
                        <TrendingUp className="w-3 h-3" />
                        {analytics.views?.this_week} this week
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Contact Clicks */}
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MousePointer className="w-4 h-4 text-green-400" />
                    <span className="text-zinc-400 text-xs">Contact Clicks</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{analytics.contact_clicks?.total || 0}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-zinc-500">Today: {analytics.contact_clicks?.today || 0}</span>
                    {analytics.contact_clicks?.this_week > 0 && (
                      <span className="text-xs text-emerald-400 flex items-center gap-0.5">
                        <TrendingUp className="w-3 h-3" />
                        {analytics.contact_clicks?.this_week} this week
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Video Plays */}
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Play className="w-4 h-4 text-purple-400" />
                    <span className="text-zinc-400 text-xs">Video Plays</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{analytics.video_plays?.total || 0}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-zinc-500">Today: {analytics.video_plays?.today || 0}</span>
                    {analytics.video_plays?.this_week > 0 && (
                      <span className="text-xs text-emerald-400 flex items-center gap-0.5">
                        <TrendingUp className="w-3 h-3" />
                        {analytics.video_plays?.this_week} this week
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Share Clicks */}
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Share2 className="w-4 h-4 text-amber-400" />
                    <span className="text-zinc-400 text-xs">Profile Shares</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{analytics.share_clicks?.total || 0}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-zinc-500">Today: {analytics.share_clicks?.today || 0}</span>
                    {analytics.share_clicks?.this_week > 0 && (
                      <span className="text-xs text-emerald-400 flex items-center gap-0.5">
                        <TrendingUp className="w-3 h-3" />
                        {analytics.share_clicks?.this_week} this week
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Period Summary */}
              <div className="bg-zinc-800/30 rounded-lg p-4">
                <h4 className="text-sm font-medium text-zinc-300 mb-3">30-Day Summary</h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-lg font-semibold text-white">{analytics.views?.this_month || 0}</div>
                    <div className="text-xs text-zinc-500">Views</div>
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-white">{analytics.contact_clicks?.this_month || 0}</div>
                    <div className="text-xs text-zinc-500">Contact Clicks</div>
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-white">{analytics.video_plays?.this_month || 0}</div>
                    <div className="text-xs text-zinc-500">Video Plays</div>
                  </div>
                </div>
              </div>
              
              {/* Engagement Rate */}
              {(analytics.views?.total || 0) > 0 && (
                <div className="mt-4 flex items-center justify-between text-sm">
                  <span className="text-zinc-400">Engagement Rate</span>
                  <span className="text-white font-medium">
                    {(((analytics.contact_clicks?.total || 0) / (analytics.views?.total || 1)) * 100).toFixed(1)}%
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Profile Score Widget */}
        <div className="mb-6">
          <ProfileScoreWidget 
            profileId={profileId}
            onSuggestionClick={(suggestion) => {
              if (suggestion.link) {
                window.location.href = suggestion.link;
              }
            }}
          />
        </div>
        
        {/* Coupon Redemption Section - Show for inactive OR active expiring soon */}
        {(subscriptionStatus !== 'active' || (daysUntilExpiry !== null && daysUntilExpiry <= 30)) && (
          <div className="mb-6 border border-zinc-700 bg-zinc-900/50 rounded-xl overflow-hidden" data-testid="coupon-redemption-section">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#C41E3A]/20 to-zinc-900 px-6 py-4 border-b border-zinc-800">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                {subscriptionStatus === 'active' ? 'Renew Your Profile' : 'Activate Your Profile'}
              </h3>
              <p className="text-zinc-400 text-sm mt-1">
                {subscriptionStatus === 'active' 
                  ? `Your profile expires in ${daysUntilExpiry} days. Renew to keep it live.`
                  : 'Choose a plan to make your profile visible to the public.'}
              </p>
            </div>
            
            <div className="p-6">
              {/* Pricing Plans */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {/* 6 Month Plan */}
                <div 
                  className={`relative border-2 rounded-xl p-5 cursor-pointer transition-all ${
                    selectedPlan === '6_months' 
                      ? 'border-[#C41E3A] bg-[#C41E3A]/10' 
                      : 'border-zinc-700 hover:border-zinc-500'
                  }`}
                  onClick={() => setSelectedPlan('6_months')}
                  data-testid="plan-6-months"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-white font-semibold">6 Months</h4>
                      <p className="text-zinc-500 text-xs">Standard Plan</p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedPlan === '6_months' ? 'border-[#C41E3A] bg-[#C41E3A]' : 'border-zinc-600'
                    }`}>
                      {selectedPlan === '6_months' && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-white">₹4,999</span>
                    <span className="text-zinc-500 text-sm">/6 months</span>
                  </div>
                  <p className="text-zinc-500 text-xs mt-2">₹833/month</p>
                </div>
                
                {/* 1 Year Plan */}
                <div 
                  className={`relative border-2 rounded-xl p-5 cursor-pointer transition-all ${
                    selectedPlan === '1_year' 
                      ? 'border-[#C41E3A] bg-[#C41E3A]/10' 
                      : 'border-zinc-700 hover:border-zinc-500'
                  }`}
                  onClick={() => setSelectedPlan('1_year')}
                  data-testid="plan-1-year"
                >
                  {/* Best Value Badge */}
                  <div className="absolute -top-3 left-4 bg-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                    BEST VALUE
                  </div>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-white font-semibold">1 Year</h4>
                      <p className="text-zinc-500 text-xs">Save ₹1,999</p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedPlan === '1_year' ? 'border-[#C41E3A] bg-[#C41E3A]' : 'border-zinc-600'
                    }`}>
                      {selectedPlan === '1_year' && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-white">₹7,999</span>
                    <span className="text-zinc-500 text-sm">/year</span>
                  </div>
                  <p className="text-zinc-500 text-xs mt-2">₹667/month</p>
                </div>
              </div>
              
              {/* Subscribe Button - WhatsApp or Request Callback */}
              <div className="space-y-3">
                <a
                  href={`https://wa.me/919003035900?text=${encodeURIComponent(`Hi, I want to activate my BrandStage Leadership profile.\n\nProfile: ${formData.leader_name || 'My Profile'}\nPlan: ${selectedPlan === '6_months' ? '6 Months - ₹4,999' : '1 Year - ₹7,999'}\nEmail: ${formData.email || ''}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white py-4 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 text-lg"
                  data-testid="whatsapp-subscribe-btn"
                >
                  <MessageSquare className="w-5 h-5" />
                  Subscribe via WhatsApp - {selectedPlan === '6_months' ? '₹4,999' : '₹7,999'}
                </a>
                
                <button
                  type="button"
                  onClick={() => setShowCallbackForm(!showCallbackForm)}
                  className="w-full bg-zinc-800 hover:bg-zinc-700 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 border border-zinc-700"
                  data-testid="callback-btn"
                >
                  <Phone className="w-4 h-4" />
                  Request a Callback
                </button>
                
                {showCallbackForm && (
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4 mt-3">
                    <p className="text-zinc-300 text-sm mb-3">Enter your phone number and we&apos;ll call you to complete the subscription.</p>
                    <div className="flex gap-2">
                      <input
                        type="tel"
                        value={callbackPhone}
                        onChange={(e) => setCallbackPhone(e.target.value)}
                        placeholder="+91 98765 43210"
                        className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-2 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                      />
                      <button
                        type="button"
                        onClick={async () => {
                          if (!callbackPhone.trim()) {
                            alert('Please enter your phone number');
                            return;
                          }
                          alert(`Thank you! We'll call you at ${callbackPhone} shortly to complete your subscription.`);
                          setShowCallbackForm(false);
                          setCallbackPhone('');
                        }}
                        className="bg-[#C41E3A] hover:bg-[#a01830] text-white px-4 py-2 rounded-lg font-medium transition-colors"
                      >
                        Submit
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              <p className="text-zinc-500 text-xs text-center mt-3">
                Secure payment. Your profile goes live immediately after confirmation.
              </p>
              
              {/* Terms Agreement */}
              <div className="mt-4 p-4 bg-zinc-800/30 border border-zinc-700 rounded-lg">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                    termsAccepted ? 'bg-[#C41E3A] border-[#C41E3A]' : 'border-zinc-600 group-hover:border-zinc-400'
                  }`}>
                    {termsAccepted && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <span className="text-zinc-400 text-sm leading-relaxed">
                    I agree that my profile will be publicly visible once activated. I have read and accept the{' '}
                    <a href="/terms" target="_blank" className="text-[#C41E3A] hover:underline">Terms of Service</a>
                    {' '}and{' '}
                    <a href="/privacy" target="_blank" className="text-[#C41E3A] hover:underline">Privacy Policy</a>.
                    I understand that BrandStage reserves the right to review and remove content that violates community guidelines.
                  </span>
                </label>
              </div>
              
              {/* Promo Code Section - Collapsible */}
              <div className="mt-6 pt-6 border-t border-zinc-800">
                <button
                  type="button"
                  onClick={() => setShowPromoInput(!showPromoInput)}
                  className="text-[#C41E3A] text-sm font-medium hover:underline flex items-center gap-1"
                >
                  <Gift className="w-4 h-4" />
                  {showPromoInput ? 'Hide promo code' : 'Have a promo code?'}
                </button>
                
                {showPromoInput && (
                  <div className="mt-4">
                    <div className="flex gap-3">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => {
                          setCouponCode(e.target.value.toUpperCase());
                          setCouponValidation(null);
                        }}
                        placeholder="Enter promo code"
                        className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 uppercase focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                        data-testid="coupon-code-input"
                      />
                      <button
                        type="button"
                        onClick={validateCoupon}
                        disabled={!couponCode.trim() || isValidatingCoupon}
                        className="bg-zinc-700 hover:bg-zinc-600 text-white px-5 py-3 rounded-lg font-medium transition-colors disabled:opacity-50"
                        data-testid="validate-coupon-btn"
                      >
                        {isValidatingCoupon ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Apply'}
                      </button>
                    </div>
                    
                    {/* Coupon Validation Result */}
                    {couponValidation && (
                      couponValidation.valid ? (
                        <div className="bg-emerald-900/30 border border-emerald-700 rounded-lg p-4 mt-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-emerald-400 font-medium flex items-center gap-2">
                              <Check className="w-5 h-5" />
                              Coupon Applied: {couponValidation.code}
                            </span>
                            <span className="text-emerald-400 font-bold">{couponValidation.discount_percent}% OFF</span>
                          </div>
                          <div className="flex items-center gap-3 text-sm">
                            <span className="text-zinc-400 line-through">₹{couponValidation.original_price}</span>
                            <span className="text-white font-bold text-lg">
                              {couponValidation.final_price === 0 ? 'FREE' : `₹${couponValidation.final_price}`}
                            </span>
                            <span className="text-zinc-500">for 6 months</span>
                          </div>
                          
                          <button
                            type="button"
                            onClick={applyCoupon}
                            disabled={isApplyingCoupon}
                            className="w-full mt-4 bg-emerald-600 hover:bg-emerald-500 text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            data-testid="activate-profile-btn"
                          >
                            {isApplyingCoupon ? (
                              <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                {subscriptionStatus === 'active' ? 'Renewing...' : 'Activating...'}
                              </>
                            ) : (
                              <>
                                <Check className="w-5 h-5" />
                                {subscriptionStatus === 'active' 
                                  ? (couponValidation.final_price === 0 ? 'Renew Profile (Free)' : `Renew Profile - Pay ₹${couponValidation.final_price}`)
                                  : (couponValidation.final_price === 0 ? 'Activate Profile (Free)' : `Activate Profile - Pay ₹${couponValidation.final_price}`)}
                              </>
                            )}
                          </button>
                        </div>
                      ) : (
                        <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 mt-4">
                          <p className="text-red-400 flex items-center gap-2">
                            <X className="w-5 h-5" />
                            {couponValidation.error || 'Invalid coupon code'}
                          </p>
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Already Active Notice - only show if not expiring soon */}
        {subscriptionStatus === 'active' && !analytics && (daysUntilExpiry === null || daysUntilExpiry > 30) && (
          <div className="mb-6 bg-emerald-900/20 border border-emerald-700 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-emerald-400" />
              <span className="text-emerald-400 font-medium">Profile is Live</span>
            </div>
            <p className="text-zinc-400 text-sm mt-1">
              Your profile is active and visible to the public
              {activeUntil && ` until ${activeUntil.toLocaleDateString()}`}.
            </p>
          </div>
        )}

        {/* Edit Form */}
        <div className="space-y-8">
          {/* Basic Info Section */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-[#C41E3A]" />
              Basic Information
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Photo */}
              <div className="md:col-span-2 flex items-center gap-6">
                <div className="relative">
                  {formData.leader_photo ? (
                    <img 
                      src={formData.leader_photo} 
                      alt="Profile" 
                      className="w-24 h-24 rounded-full object-cover border-2 border-zinc-700"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-zinc-800 flex items-center justify-center border-2 border-zinc-700">
                      <User className="w-10 h-10 text-zinc-600" />
                    </div>
                  )}
                  {uploadingPhoto && (
                    <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                  )}
                </div>
                <div>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => photoInputRef.current?.click()}
                    className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm transition-colors"
                    disabled={uploadingPhoto}
                  >
                    <Upload className="w-4 h-4 inline mr-2" />
                    {uploadingPhoto ? 'Uploading...' : 'Change Photo'}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.leader_name}
                  onChange={(e) => handleInputChange('leader_name', e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-[#C41E3A] focus:border-transparent"
                  data-testid="edit-leader-name"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-2">Title *</label>
                <input
                  type="text"
                  value={formData.leader_title}
                  onChange={(e) => handleInputChange('leader_title', e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-[#C41E3A] focus:border-transparent"
                  placeholder="e.g., CEO & Founder"
                  data-testid="edit-leader-title"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-2">Company</label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-[#C41E3A] focus:border-transparent"
                  data-testid="edit-company-name"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-2">Tagline</label>
                <input
                  type="text"
                  value={formData.tagline}
                  onChange={(e) => handleInputChange('tagline', e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-[#C41E3A] focus:border-transparent"
                  placeholder="e.g., Building the Future of Technology"
                />
              </div>
              
              {/* Professional Category */}
              <div className="md:col-span-2">
                <label className="block text-sm text-zinc-400 mb-2">Professional Category</label>
                <select
                  value={formData.profile_category}
                  onChange={(e) => handleInputChange('profile_category', e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-[#C41E3A] focus:border-transparent"
                  data-testid="profile-category-select"
                >
                  {personalCategories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.label} - {cat.description}
                    </option>
                  ))}
                </select>
                <p className="text-zinc-500 text-xs mt-1">
                  This customizes the display of your profile for your target audience
                </p>
              </div>
            </div>
          </div>

          {/* Expertise Section */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-[#C41E3A]" />
              Expertise & Experience
            </h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Areas of Expertise</label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {expertiseOptions.map(exp => (
                    <button
                      key={exp}
                      type="button"
                      onClick={() => {
                        const current = formData.expertise_areas || [];
                        if (current.includes(exp)) {
                          handleInputChange('expertise_areas', current.filter(e => e !== exp));
                        } else {
                          handleInputChange('expertise_areas', [...current, exp]);
                        }
                      }}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        (formData.expertise_areas || []).includes(exp)
                          ? 'bg-[#C41E3A] text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {exp}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-2">Industries</label>
                <div className="flex flex-wrap gap-2">
                  {industryOptions.map(ind => (
                    <button
                      key={ind}
                      type="button"
                      onClick={() => {
                        const current = formData.industries || [];
                        if (current.includes(ind)) {
                          handleInputChange('industries', current.filter(i => i !== ind));
                        } else {
                          handleInputChange('industries', [...current, ind]);
                        }
                      }}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        (formData.industries || []).includes(ind)
                          ? 'bg-blue-600 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {ind}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-2">Key Achievements</label>
                <textarea
                  value={formData.achievements}
                  onChange={(e) => handleInputChange('achievements', e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-[#C41E3A] focus:border-transparent"
                  rows={4}
                  placeholder="Describe your major achievements..."
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-2">Vision Statement</label>
                <textarea
                  value={formData.vision_statement}
                  onChange={(e) => handleInputChange('vision_statement', e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-[#C41E3A] focus:border-transparent"
                  rows={3}
                  placeholder="Your vision for the future..."
                />
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#C41E3A]" />
              Contact Information
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => handleInputChange('contact_email', e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-11 pr-4 py-3 text-white focus:ring-2 focus:ring-[#C41E3A] focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-2">Phone</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-11 pr-4 py-3 text-white focus:ring-2 focus:ring-[#C41E3A] focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-2">LinkedIn</label>
                <div className="relative">
                  <Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input
                    type="url"
                    value={formData.linkedin_url}
                    onChange={(e) => handleInputChange('linkedin_url', e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-11 pr-4 py-3 text-white focus:ring-2 focus:ring-[#C41E3A] focus:border-transparent"
                    placeholder="https://linkedin.com/in/..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-2">Twitter</label>
                <div className="relative">
                  <Twitter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input
                    type="url"
                    value={formData.twitter_url}
                    onChange={(e) => handleInputChange('twitter_url', e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-11 pr-4 py-3 text-white focus:ring-2 focus:ring-[#C41E3A] focus:border-transparent"
                    placeholder="https://twitter.com/..."
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm text-zinc-400 mb-2">Website</label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-11 pr-4 py-3 text-white focus:ring-2 focus:ring-[#C41E3A] focus:border-transparent"
                    placeholder="https://..."
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-2">YouTube Channel</label>
                <div className="relative">
                  <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input
                    type="url"
                    value={formData.youtube_url}
                    onChange={(e) => handleInputChange('youtube_url', e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-11 pr-4 py-3 text-white focus:ring-2 focus:ring-[#C41E3A] focus:border-transparent"
                    placeholder="https://youtube.com/@..."
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Instagram</label>
                <div className="relative">
                  <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input
                    type="url"
                    value={formData.instagram_url}
                    onChange={(e) => handleInputChange('instagram_url', e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-11 pr-4 py-3 text-white focus:ring-2 focus:ring-[#C41E3A] focus:border-transparent"
                    placeholder="https://instagram.com/..."
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Published Articles & Media Section */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-bold flex items-center gap-2 mb-2">
              <BookOpen className="w-5 h-5 text-[#C41E3A]" />
              Publications & Media
            </h2>
            <p className="text-zinc-400 text-sm mb-4">
              Add links to your published articles, interviews, podcasts, and media appearances.
            </p>
            
            {/* Article List */}
            {formData.published_articles.length > 0 && (
              <div className="space-y-3 mb-4">
                {formData.published_articles.map((article, index) => (
                  <div key={index} className="flex items-center gap-3 bg-zinc-800/50 border border-zinc-700 rounded-lg p-3">
                    <span className="text-zinc-500 text-xs uppercase px-2 py-1 bg-zinc-700 rounded">
                      {article.category || 'Article'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate">{article.title}</p>
                      <p className="text-zinc-500 text-xs truncate">{article.publication}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          published_articles: prev.published_articles.filter((_, i) => i !== index)
                        }));
                      }}
                      className="text-zinc-500 hover:text-red-400 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add New Article Form */}
            <div className="border border-dashed border-zinc-700 rounded-lg p-4">
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Category *</label>
                  <select
                    id="new-article-category"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-[#C41E3A] focus:border-transparent"
                    defaultValue=""
                  >
                    <option value="" disabled>Select type...</option>
                    <option value="blog">Blog Post</option>
                    <option value="news">News Article</option>
                    <option value="announcement">Announcement</option>
                    <option value="product_launch">Product Launch</option>
                    <option value="speech">Speech / Talk</option>
                    <option value="podcast">Podcast Appearance</option>
                    <option value="interview">Published Interview</option>
                    <option value="award">Award / Recognition</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Publication / Source</label>
                  <input
                    type="text"
                    id="new-article-publication"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-[#C41E3A] focus:border-transparent"
                    placeholder="Forbes, LinkedIn, etc."
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm text-zinc-400 mb-2">Title *</label>
                <input
                  type="text"
                  id="new-article-title"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-[#C41E3A] focus:border-transparent"
                  placeholder="Article title..."
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm text-zinc-400 mb-2">URL *</label>
                <input
                  type="url"
                  id="new-article-url"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-[#C41E3A] focus:border-transparent"
                  placeholder="https://..."
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  const category = document.getElementById('new-article-category').value;
                  const title = document.getElementById('new-article-title').value;
                  const url = document.getElementById('new-article-url').value;
                  const publication = document.getElementById('new-article-publication').value;
                  
                  if (!category || !title || !url) {
                    setError('Please fill in category, title, and URL');
                    return;
                  }
                  
                  setFormData(prev => ({
                    ...prev,
                    published_articles: [...prev.published_articles, {
                      category,
                      title,
                      url,
                      publication: publication || ''
                    }]
                  }));
                  
                  // Clear form
                  document.getElementById('new-article-category').value = '';
                  document.getElementById('new-article-title').value = '';
                  document.getElementById('new-article-url').value = '';
                  document.getElementById('new-article-publication').value = '';
                }}
                className="w-full py-2 border border-[#C41E3A] text-[#C41E3A] hover:bg-[#C41E3A]/10 rounded-lg transition-colors text-sm font-medium"
              >
                + Add Article
              </button>
            </div>
          </div>

          {/* AI Sections */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#C41E3A]" />
                AI-Generated Sections
              </h2>
              <button
                onClick={regenerateSections}
                disabled={isGenerating}
                className="px-4 py-2 bg-[#C41E3A] hover:bg-[#A01830] text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Regenerate Sections
                  </>
                )}
              </button>
            </div>
            
            {formData.profile_sections ? (
              <div className="space-y-4">
                {formData.profile_sections.executive_summary && (
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Executive Summary</label>
                    <textarea
                      value={formData.profile_sections.executive_summary}
                      onChange={(e) => handleInputChange('profile_sections', {
                        ...formData.profile_sections,
                        executive_summary: e.target.value
                      })}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white text-sm"
                      rows={4}
                    />
                  </div>
                )}
                {formData.profile_sections.my_journey && (
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">My Journey</label>
                    <textarea
                      value={formData.profile_sections.my_journey}
                      onChange={(e) => handleInputChange('profile_sections', {
                        ...formData.profile_sections,
                        my_journey: e.target.value
                      })}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white text-sm"
                      rows={4}
                    />
                  </div>
                )}
                {formData.profile_sections.my_expertise && (
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">My Expertise</label>
                    <textarea
                      value={formData.profile_sections.my_expertise}
                      onChange={(e) => handleInputChange('profile_sections', {
                        ...formData.profile_sections,
                        my_expertise: e.target.value
                      })}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white text-sm"
                      rows={4}
                    />
                  </div>
                )}
                {formData.profile_sections.my_vision && (
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">My Vision</label>
                    <textarea
                      value={formData.profile_sections.my_vision}
                      onChange={(e) => handleInputChange('profile_sections', {
                        ...formData.profile_sections,
                        my_vision: e.target.value
                      })}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white text-sm"
                      rows={4}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-500">
                <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No AI sections generated yet.</p>
                <p className="text-sm">Click &quot;Regenerate Sections&quot; to create AI content.</p>
              </div>
            )}
          </div>

          {/* Featured Videos Section */}
          <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Video className="w-5 h-5 text-[#C41E3A]" />
                Featured Videos
                <span className="text-zinc-500 text-sm font-normal">(Optional)</span>
              </h2>
              {formData.additional_videos.length < 5 && (
                <button
                  type="button"
                  onClick={addVideoSlot}
                  className="text-[#C41E3A] text-sm font-medium hover:underline"
                >
                  + Add Video
                </button>
              )}
            </div>
            
            <p className="text-zinc-400 text-sm mb-4">
              Add YouTube videos to showcase your talks, interviews, or media appearances.
            </p>
            
            {formData.additional_videos.length === 0 ? (
              <div className="border-2 border-dashed border-zinc-700 rounded-lg p-6 text-center">
                <Youtube className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                <p className="text-zinc-500 text-sm">No videos added yet</p>
                <button
                  type="button"
                  onClick={addVideoSlot}
                  className="text-[#C41E3A] text-sm font-medium mt-2 hover:underline"
                >
                  + Add your first video
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {formData.additional_videos.map((video, index) => (
                  <div key={index} className="border border-zinc-700 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-zinc-400 text-xs">Video {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeVideo(index)}
                        className="text-red-400 text-xs hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <input
                        type="url"
                        value={video.url}
                        onChange={(e) => updateVideo(index, 'url', e.target.value)}
                        placeholder="YouTube URL"
                        className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-white text-sm placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                      />
                      <input
                        type="text"
                        value={video.title}
                        onChange={(e) => updateVideo(index, 'title', e.target.value)}
                        placeholder="Video Title"
                        className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-white text-sm placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Opportunity Showcase Pages */}
          <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
            <OpportunityManager
              profileId={profileId}
              profileSlug={originalSlug}
              isAdmin={user?.email?.toLowerCase() === 'brandstagevideo@gmail.com'}
            />
          </div>

          {/* Testimonials Section */}
          <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-[#C41E3A]" />
                Testimonials
                <span className="text-zinc-500 text-sm font-normal">(Optional)</span>
              </h2>
              <button
                type="button"
                onClick={() => setShowTestimonialModal(true)}
                className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors"
                data-testid="request-testimonial-btn"
              >
                <Send className="w-3.5 h-3.5" />
                Request Testimonial
              </button>
            </div>
            
            <p className="text-zinc-400 text-sm mb-4">
              Collect video testimonials from colleagues and clients. They&apos;ll receive an email with a link to record their video.
            </p>
            
            {testimonials.length === 0 ? (
              <div className="border-2 border-dashed border-zinc-700 rounded-lg p-6 text-center">
                <MessageSquare className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                <p className="text-zinc-500 text-sm">No testimonials yet</p>
                <p className="text-zinc-600 text-xs mt-1">
                  Request testimonials from colleagues to build credibility
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {testimonials.map((testimonial) => (
                  <div 
                    key={testimonial.id} 
                    className="border border-zinc-700 rounded-lg p-4 bg-zinc-800/30"
                    data-testid="testimonial-item"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="w-16 h-12 bg-zinc-700 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center">
                          {testimonial.video_url ? (
                            <Play className="w-5 h-5 text-zinc-400" />
                          ) : (
                            <Clock className="w-5 h-5 text-zinc-500" />
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium">{testimonial.contact_name}</p>
                          <p className="text-zinc-400 text-sm">{testimonial.contact_email}</p>
                          
                          <div className="flex items-center gap-2 mt-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              testimonial.status === 'approved' 
                                ? 'bg-green-500/20 text-green-400' 
                                : testimonial.status === 'recorded' || testimonial.status === 'pending_review'
                                ? 'bg-amber-500/20 text-amber-400'
                                : testimonial.status === 'rejected'
                                ? 'bg-red-500/20 text-red-400'
                                : 'bg-zinc-600/30 text-zinc-400'
                            }`}>
                              {testimonial.status === 'pending_recording' ? 'Pending Recording' :
                               testimonial.status === 'recorded' ? 'Awaiting Approval' :
                               testimonial.status === 'pending_review' ? 'Awaiting Approval' :
                               testimonial.status === 'approved' ? 'Approved' :
                               testimonial.status === 'rejected' ? 'Rejected' : 'Pending'}
                            </span>
                            
                            {testimonial.video_url && (
                              <a 
                                href={testimonial.video_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-[#C41E3A] text-xs hover:underline flex items-center gap-1"
                              >
                                <Play className="w-3 h-3" /> Preview
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {testimonial.status === 'approved' && (
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-zinc-500 text-xs">Visible on Profile</span>
                          <button
                            type="button"
                            onClick={() => toggleTestimonialVisibility(testimonial.id)}
                            disabled={togglingVisibility === testimonial.id}
                            className={`relative w-12 h-6 rounded-full transition-colors ${
                              testimonial.is_visible ? 'bg-green-600' : 'bg-zinc-600'
                            } ${togglingVisibility === testimonial.id ? 'opacity-50' : ''}`}
                            data-testid={`visibility-toggle-${testimonial.id}`}
                          >
                            <span 
                              className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                                testimonial.is_visible ? 'translate-x-7' : 'translate-x-1'
                              }`}
                            />
                          </button>
                          <span className={`text-xs ${testimonial.is_visible ? 'text-green-400' : 'text-zinc-500'}`}>
                            {testimonial.is_visible ? 'Showing' : 'Hidden'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <p className="text-zinc-500 text-xs mt-3">
              {testimonials.length}/5 testimonials
            </p>
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="px-6 py-3 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-8 py-3 bg-[#C41E3A] hover:bg-[#A01830] text-white rounded-lg font-bold transition-colors disabled:opacity-50 flex items-center gap-2"
              data-testid="save-leadership-profile-btn"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Testimonial Request Modal */}
      {showTestimonialModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Send className="w-5 h-5 text-[#C41E3A]" />
                Request Testimonial
              </h3>
              <button
                onClick={() => setShowTestimonialModal(false)}
                className="text-zinc-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-zinc-400 text-sm mb-4">
              We&apos;ll send an email to your contact with a link to record a video testimonial.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-1">Contact Name *</label>
                <input
                  type="text"
                  value={testimonialRequest.contact_name}
                  onChange={(e) => setTestimonialRequest(prev => ({ ...prev, contact_name: e.target.value }))}
                  placeholder="John Smith"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                  data-testid="testimonial-contact-name"
                />
              </div>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-1">Contact Email *</label>
                <input
                  type="email"
                  value={testimonialRequest.contact_email}
                  onChange={(e) => setTestimonialRequest(prev => ({ ...prev, contact_email: e.target.value }))}
                  placeholder="john@company.com"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                  data-testid="testimonial-contact-email"
                />
              </div>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-1">Personal Message (Optional)</label>
                <textarea
                  value={testimonialRequest.message}
                  onChange={(e) => setTestimonialRequest(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Hi John, I'd appreciate if you could share your experience..."
                  rows={3}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 resize-none"
                  data-testid="testimonial-message"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowTestimonialModal(false)}
                className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-2.5 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={requestTestimonial}
                disabled={isRequestingTestimonial || !testimonialRequest.contact_name || !testimonialRequest.contact_email}
                className="flex-1 bg-[#C41E3A] hover:bg-[#A01830] text-white py-2.5 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="send-testimonial-request-btn"
              >
                {isRequestingTestimonial ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Request
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
